import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Header = ({ onMenuClick, isMobile }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();
        logout();
    };

    const handleProfile = () => {
        handleClose();
        navigate('/profile');
    };

    const userName = user?.nombre || user?.usu_nombre || 'Usuario';
    const userInitial = userName.charAt(0).toUpperCase();

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
                color: '#001F3F',
                borderBottom: '1px solid rgba(0, 31, 63, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 31, 63, 0.06)',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: 'blur(10px)',
            }}
        >
            <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: '70px !important' }}>
                {isMobile && (
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={onMenuClick}
                        sx={{ 
                            mr: 2,
                            color: '#001F3F',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 31, 63, 0.08)',
                            }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                {/* Nombre de la Empresa */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography 
                        variant="h5" 
                        component="div" 
                        sx={{ 
                            fontWeight: 700,
                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                            background: 'linear-gradient(135deg, #001F3F 0%, #3A6B7E 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            letterSpacing: '-0.5px',
                        }}
                    >
                        IESS
                    </Typography>
                </Box>

                {/* Información del Usuario */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: '#001F3F',
                                fontSize: '0.95rem',
                                lineHeight: 1.2,
                            }}
                        >
                            {userName}
                        </Typography>
                        {user?.usu_rol && (
                            <Chip
                                label={user.usu_rol}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    backgroundColor: 'rgba(58, 107, 126, 0.1)',
                                    color: '#3A6B7E',
                                    mt: 0.5,
                                    '& .MuiChip-label': {
                                        px: 1,
                                    }
                                }}
                            />
                        )}
                    </Box>

                    <IconButton
                        size="medium"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleMenu}
                        sx={{ 
                            p: 0,
                            border: '2px solid rgba(0, 31, 63, 0.1)',
                            borderRadius: '50%',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: '#3A6B7E',
                                transform: 'scale(1.05)',
                                boxShadow: '0 4px 12px rgba(58, 107, 126, 0.3)',
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                background: 'linear-gradient(135deg, #3A6B7E 0%, #001F3F 100%)',
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(58, 107, 126, 0.3)',
                            }}
                        >
                            {userInitial}
                        </Avatar>
                    </IconButton>

                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={open}
                        onClose={handleClose}
                        PaperProps={{
                            elevation: 8,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 4px 16px rgba(0, 31, 63, 0.15))',
                                mt: 1.5,
                                minWidth: 220,
                                borderRadius: 2,
                                border: '1px solid rgba(0, 31, 63, 0.08)',
                                '& .MuiMenuItem-root': {
                                    px: 2,
                                    py: 1.5,
                                    fontSize: '0.95rem',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 31, 63, 0.05)',
                                    }
                                },
                                '& .MuiListItemIcon-root': {
                                    minWidth: 40,
                                    color: '#3A6B7E',
                                },
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    borderLeft: '1px solid rgba(0, 31, 63, 0.08)',
                                    borderTop: '1px solid rgba(0, 31, 63, 0.08)',
                                    zIndex: 0,
                                },
                            },
                        }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 31, 63, 0.08)' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#001F3F' }}>
                                {userName}
                            </Typography>
                            {user?.usu_rol && (
                                <Typography variant="caption" sx={{ color: '#3A6B7E', fontSize: '0.75rem' }}>
                                    {user.usu_rol}
                                </Typography>
                            )}
                        </Box>
                        <MenuItem onClick={handleProfile}>
                            <ListItemIcon>
                                <AccountCircle fontSize="small" />
                            </ListItemIcon>
                            Mi Perfil
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem onClick={handleLogout} sx={{ color: '#d32f2f' }}>
                            <ListItemIcon>
                                <Logout fontSize="small" sx={{ color: '#d32f2f' }} />
                            </ListItemIcon>
                            Cerrar Sesión
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
